import type { ComponentType, ReactNode } from "react";
import { Link } from "wouter";
import { ChevronDown, ChevronRight, Lock, Megaphone } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface Props {
  sidebarOpen: boolean;
  isMobile: boolean;
  expanded: boolean;
  active: boolean;
  location: string;
  locked: boolean;
  onToggle: () => void;
  onUpgrade: () => void;
  items: { label: string; href: string; icon: ComponentType<{ className?: string }>; testid?: string }[];
  renderFavorite: (href: string) => ReactNode;
}

export function MarketingNavigation(props: Props) {
  const { sidebarOpen, isMobile, expanded, active, location, locked, onToggle, onUpgrade, items, renderFavorite } = props;
  const links = (collapsed: boolean) => items.map(({ label, href, icon: Icon, testid }) => (
    <Link key={href} href={href}>
      <span
        className={`group flex items-center gap-3 py-2 text-sm cursor-pointer ${collapsed ? "px-2" : "pl-9 pr-2"} ${location.startsWith(href) ? "text-gray-900 font-medium bg-gray-50" : "text-gray-600 hover:bg-gray-50"}`}
        data-testid={testid}
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1">{label}</span>
        {!collapsed && renderFavorite(href)}
      </span>
    </Link>
  ));

  if (locked) return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={onUpgrade} className={`w-full flex items-center gap-3 py-2.5 text-sm text-gray-400 ${sidebarOpen ? "px-4" : "justify-center"}`} data-testid="link-sidebar-marketing-locked" aria-label="Marketing (locked)">
          <Megaphone className="h-4 w-4" />
          {sidebarOpen && <span className="flex-1 text-left">Marketing</span>}
          {sidebarOpen && <Lock className="h-3.5 w-3.5 text-amber-500" />}
        </button>
      </TooltipTrigger>
      {!sidebarOpen && !isMobile && <TooltipContent side="right">Marketing (locked)</TooltipContent>}
    </Tooltip>
  );

  if (!sidebarOpen && !isMobile) return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button type="button" className={`w-full flex justify-center py-2.5 ${active ? "bg-gray-50 text-gray-900" : "text-gray-600"}`} data-testid="button-sidebar-marketing" aria-label="Marketing navigation group">
              <Megaphone className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">Marketing</TooltipContent>
      </Tooltip>
      <PopoverContent side="right" align="start" className="w-52 p-1">
        <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Marketing</p>
        {links(true)}
      </PopoverContent>
    </Popover>
  );

  return (
    <>
      <button type="button" onClick={onToggle} className={`w-full flex items-center gap-2 px-4 py-1.5 mt-1 hover:bg-gray-50 ${active ? "bg-gray-50" : ""}`} data-testid="button-sidebar-marketing" aria-expanded={expanded}>
        <Megaphone className="h-3.5 w-3.5 text-gray-400" />
        <span className="flex-1 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">Marketing</span>
        {expanded ? <ChevronDown className="h-3 w-3 text-gray-400" /> : <ChevronRight className="h-3 w-3 text-gray-400" />}
      </button>
      {expanded && links(false)}
    </>
  );
}